"use client";

import { useLayoutEffect, useRef, type MutableRefObject, type Ref } from "react";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HUB_OVERLAY_BACKDROP, HUB_OVERLAY_MASK } from "@/components/hub/hub-overlay-backdrop";
import { cn } from "@/lib/utils";

export const ACCOUNT_TAB_HEADER_HEIGHT_VAR = "--profile-tab-header-height";

type AccountTabMobileHeaderProps = {
  title: string;
  onBackAction: () => void;
  backAriaLabel: string;
  headerRef?: Ref<HTMLElement>;
  measureHeight?: boolean;
  className?: string;
  zIndexClassName?: string;
};

export function AccountTabMobileHeader({
  title,
  onBackAction,
  backAriaLabel,
  headerRef: headerRefProp,
  measureHeight = true,
  className,
  zIndexClassName = "z-50",
}: AccountTabMobileHeaderProps) {
  const internalRef = useRef<HTMLElement>(null);

  const setRef = (node: HTMLElement | null) => {
    internalRef.current = node;
    if (!headerRefProp) return;
    if (typeof headerRefProp === "function") {
      headerRefProp(node);
      return;
    }
    (headerRefProp as MutableRefObject<HTMLElement | null>).current = node;
  };

  useLayoutEffect(() => {
    if (!measureHeight) return;
    const el = internalRef.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => {
      root.style.setProperty(
        ACCOUNT_TAB_HEADER_HEIGHT_VAR,
        `${Math.ceil(el.getBoundingClientRect().height)}px`,
      );
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener("resize", update, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      root.style.removeProperty(ACCOUNT_TAB_HEADER_HEIGHT_VAR);
    };
  }, [measureHeight]);

  return (
    <header
      ref={setRef}
      data-profile-tab-header
      className={cn(
        "pointer-events-none fixed top-0 right-0 left-0 isolate safe-area-top md:hidden",
        zIndexClassName,
        className,
      )}
    >
      <div aria-hidden className={HUB_OVERLAY_BACKDROP} style={HUB_OVERLAY_MASK} />
      <div className="relative z-1">
        <div className="pointer-events-auto flex h-14 items-center gap-2 px-4">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-full hover:bg-muted/80 dark:hover:bg-white/10"
            onClick={onBackAction}
            aria-label={backAriaLabel}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight text-foreground">
            {title}
          </h1>
        </div>
      </div>
    </header>
  );
}

export function accountTabHeaderPaddingTop(extra = "0.75rem") {
  return `calc(var(${ACCOUNT_TAB_HEADER_HEIGHT_VAR}, 3.5rem) + ${extra})`;
}
